import { useEffect, useState } from "react";
import { ArrowRight, Building2, RotateCw, ShieldCheck } from "lucide-react";
import api from "@/api";
import { Button } from "@/components/ui/button";

const PRIORITY = ["cleanmates", "rover", "limpio", "pestgenis", "stainguard"];

export default function WorkspaceChooser() {
  const [workspaces, setWorkspaces] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const lastTenant = localStorage.getItem("cm_last_tenant");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/platform/workspaces");
      const priority = (tenant) => {
        const index = PRIORITY.indexOf(tenant.slug);
        return index === -1 ? PRIORITY.length : index;
      };
      setWorkspaces(data.filter((tenant) => tenant.status === "active").sort((a, b) => priority(a) - priority(b) || a.name.localeCompare(b.name)));
    } catch {
      setError("Workspaces could not be loaded.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openWorkspace = (tenant) => {
    localStorage.setItem("cm_active_tenant", tenant.id);
    localStorage.setItem("cm_last_tenant", tenant.id);
    sessionStorage.removeItem("cm_impersonating");
    window.location.assign("/admin");
  };

  const openPlatform = () => {
    localStorage.removeItem("cm_active_tenant");
    sessionStorage.removeItem("cm_impersonating");
    window.location.assign("/admin/platform");
  };

  return (
    <main className="min-h-screen bg-background px-5 py-10 md:px-10" data-testid="workspace-chooser">
      <div className="mx-auto max-w-5xl">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4 border-b pb-5">
          <div>
            <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              <ShieldCheck size={15} /> Super Admin
            </div>
            <h1 className="font-heading text-2xl font-bold">Choose a workspace</h1>
          </div>
          <Button variant="outline" data-testid="open-platform-console" onClick={openPlatform}>
            <Building2 size={16} className="mr-2" /> Platform Console
          </Button>
        </div>

        {error && (
          <div role="alert" className="mb-5 flex flex-wrap items-center justify-between gap-3 border border-danger bg-danger px-4 py-3 text-sm">
            <span>{error}</span>
            <Button size="sm" variant="outline" onClick={load}><RotateCw size={14} className="mr-2" /> Retry</Button>
          </div>
        )}

        {loading ? <div className="py-10 text-sm text-muted-foreground">Loading workspaces…</div> : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {workspaces.map((tenant) => (
              <button key={tenant.id} type="button" onClick={() => openWorkspace(tenant)}
                data-testid={`choose-workspace-${tenant.slug}`}
                className="group flex min-h-24 items-center gap-4 border bg-card px-4 py-4 text-left transition-colors hover:border-primary/50 hover:bg-accent/40">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center text-lg font-bold text-inverse" style={{ background: tenant.brand_color || "hsl(var(--primary))" }}>
                  {tenant.name[0]}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-semibold">Open {tenant.name}</span>
                  <span className="mt-1 block text-xs capitalize text-muted-foreground">{tenant.industry_type} workspace{tenant.id === lastTenant ? " · Last opened" : ""}</span>
                </span>
                <ArrowRight size={17} className="shrink-0 text-muted-foreground transition-transform group-hover:translate-x-1" />
              </button>
            ))}
            {!workspaces.length && !error && <div className="py-8 text-sm text-muted-foreground">No active workspaces.</div>}
          </div>
        )}
      </div>
    </main>
  );
}